/**
 * EU-587 — Advanced Matching Meta para as tags de conversão do dataLayer V2
 * (`sign_up` / `generate_lead`). O front NUNCA hasheia PII e NUNCA fala direto
 * com o Meta: aqui só garantimos que o payload client-side sai completo —
 * `fbc`/`fbp` (cookies do Meta, ou `fbc` montado a partir do `fbclid` da URL)
 * + `event_source_url`. O container server-side do GTM hasheia o `user_data`,
 * adiciona ip/user-agent, repassa o `event_id` (dedup Web/Server) e envia à
 * Conversions API.
 *
 * O clique no anúncio pode acontecer numa página diferente de onde o lead é
 * finalizado — por isso `captureFbclid` roda no BOOT do SPA e persiste o
 * `fbclid` como cookie de primeira parte `_fbc` (formato oficial
 * `fb.1.<timestamp>.<fbclid>`), para o evento final encontrá-lo mesmo após
 * navegação/reload.
 */

const FBC_COOKIE = '_fbc';
const FBP_COOKIE = '_fbp';
/** 90 dias — janela de atribuição usual do Meta. */
const FBC_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

/** Contexto extra das tags de conversão (GTM-004). */
export interface ConversionContext {
  fbc: string | null;
  fbp: string | null;
  event_source_url: string | null;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  try {
    for (const part of document.cookie.split(';')) {
      const eq = part.indexOf('=');
      if (eq === -1) continue;
      if (part.slice(0, eq).trim() === name) {
        const value = part.slice(eq + 1).trim();
        return value === '' ? null : decodeURIComponent(value);
      }
    }
  } catch {
    /* cookies bloqueados — degrada para null */
  }
  return null;
}

function readFbclid(search: string): string | null {
  try {
    const fbclid = new URLSearchParams(search).get('fbclid');
    return fbclid && fbclid.trim() !== '' ? fbclid.trim() : null;
  } catch {
    return null;
  }
}

/** Formato oficial do Meta: `fb.1.<timestamp ms>.<fbclid>`. */
export function buildFbcFromFbclid(fbclid: string): string {
  return `fb.1.${Date.now()}.${fbclid}`;
}

/**
 * Persiste o `fbclid` da URL como cookie de primeira parte `_fbc` (quando o
 * pixel do Meta não o criou). Roda no boot do SPA — o lead pode ser concluído
 * em outra página/rota, e o evento final ainda encontra o fbc. O fbclid mais
 * recente vence (recomendação do Meta). No-op sem `fbclid` na URL.
 */
export function captureFbclid(search: string = window.location.search): void {
  if (typeof document === 'undefined') return;
  const fbclid = readFbclid(search);
  if (!fbclid) return;
  try {
    document.cookie = `${FBC_COOKIE}=${encodeURIComponent(buildFbcFromFbclid(fbclid))}; max-age=${FBC_MAX_AGE_SECONDS}; path=/; SameSite=Lax`;
  } catch {
    /* cookies bloqueados — o fallback on-the-fly de readMetaBrowserIds cobre */
  }
}

/**
 * Lê `{ fbc, fbp }` — cookies `_fbc`/`_fbp` do Meta; sem `_fbc`, monta o fbc a
 * partir do `fbclid` da URL atual (clique → conversão na mesma página, sem
 * pixel). Campos ausentes saem como `null` (nunca `""` — GTM-007).
 */
export function readMetaBrowserIds(search: string = window.location.search): {
  fbc: string | null;
  fbp: string | null;
} {
  const cookieFbc = readCookie(FBC_COOKIE);
  const fbclid = cookieFbc ? null : readFbclid(search);
  return {
    fbc: cookieFbc ?? (fbclid ? buildFbcFromFbclid(fbclid) : null),
    fbp: readCookie(FBP_COOKIE),
  };
}

/** URL completa da página no momento do evento (`event_source_url`). */
export function getEventSourceUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.location.href;
  } catch {
    return null;
  }
}

/**
 * Agrega o contexto de Advanced Matching para o payload das tags de conversão
 * (`sign_up` / `generate_lead`) — consumido pelos pushes em
 * `datalayer-events.ts`.
 */
export function conversionContext(
  search: string = typeof window === 'undefined' ? '' : window.location.search
): ConversionContext {
  const { fbc, fbp } = readMetaBrowserIds(search);
  return { fbc, fbp, event_source_url: getEventSourceUrl() };
}
